import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDialog, Badge, Button, KeyValueList, Money } from '@/components/ui';
import { dispatchBalanceRefresh } from '@/utils/balanceRefresh';
import { usePendingRedeemRequests } from './usePendingRedeemRequests';
import { approveRedeemRequest, declineRedeemRequest, type RedeemApprovalRequest } from './redeemApprovalService';

type Outcome = { kind: 'approved' | 'declined' | 'error'; request: RedeemApprovalRequest; message?: string };

function secondsLeft(iso: string | null, now: number): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : Math.max(0, Math.ceil((t - now) / 1000));
}

function clock(total: number): string {
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

function formatCredits(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(Number(value) || 0);
}

function formatUsd(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD' }).format(Number(value) || 0);
}

/**
 * Blocking prompt asking the Player to approve or decline a redeem their shop started.
 * Mounted once in BaseLayout, so it shows on every page. AlertDialog sits on the blocking
 * layer, above every dialog, popover and toast, and cannot be closed without an answer
 * (no close button, Escape or backdrop click).
 */
export default function RedeemApprovalGate() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'es' ? 'es-US' : 'en-US';
  const { requests, dismiss, refresh } = usePendingRedeemRequests();
  const [busy, setBusy] = useState<null | 'approve' | 'decline'>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const current = requests[0] ?? null;
  const shown = outcome?.request ?? current;
  const remaining = current ? secondsLeft(current.expires_at, now) : 0;

  useEffect(() => {
    if (!current || outcome) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [current, outcome]);

  useEffect(() => {
    // Expired locally: drop it (the server marks it expired on its own).
    if (current && !outcome && remaining === 0 && current.expires_at) dismiss(current.id);
  }, [current, outcome, remaining, dismiss]);

  if (!shown) return null;

  const decide = async (action: 'approve' | 'decline') => {
    if (!current || busy) return;
    setBusy(action);
    const res = action === 'approve' ? await approveRedeemRequest(current.id) : await declineRedeemRequest(current.id);
    setBusy(null);
    dismiss(current.id);
    if (res.ok) {
      if (action === 'approve') dispatchBalanceRefresh();
      setOutcome({ kind: action === 'approve' ? 'approved' : 'declined', request: res.request });
      return;
    }
    const message =
      res.code === 'REDEEM_REQUEST_EXPIRED'
        ? t('redeemApproval.errorExpired')
        : res.code === 'REDEEM_REQUEST_NOT_PENDING' || res.code === 'REDEEM_REQUEST_NOT_FOUND'
          ? t('redeemApproval.errorHandled')
          : res.message;
    setOutcome({ kind: 'error', request: current, message });
  };

  const closeOutcome = () => {
    setOutcome(null);
    void refresh();
  };

  const credits = formatCredits(shown.credits, locale);
  const payout = formatUsd(shown.cash_paid, locale);

  const outcomeTitle = outcome
    ? outcome.kind === 'approved'
      ? t('redeemApproval.approvedTitle')
      : outcome.kind === 'declined'
        ? t('redeemApproval.declinedTitle')
        : t('redeemApproval.errorTitle')
    : null;
  const outcomeBody = outcome
    ? outcome.kind === 'approved'
      ? t('redeemApproval.approvedBody', { payout, shop: outcome.request.shop_name })
      : outcome.kind === 'declined'
        ? t('redeemApproval.declinedBody')
        : outcome.message
    : null;

  if (outcome) {
    return (
      <AlertDialog
        open
        title={<span id="redeem-approval-title">{outcomeTitle}</span>}
        description={
          <span id="redeem-approval-body" data-testid="redeem-approval-outcome" data-kind={outcome.kind}>
            {outcomeBody}
          </span>
        }
        actions={
          <Button variant="primary" silent onClick={closeOutcome}>
            {t('redeemApproval.ok')}
          </Button>
        }
      />
    );
  }

  return (
    <AlertDialog
      open
      title={<span id="redeem-approval-title">{t('redeemApproval.title')}</span>}
      description={<span id="redeem-approval-body">{t('redeemApproval.body', { shop: shown.shop_name, credits })}</span>}
      actions={
        <>
          <Button variant="secondary" silent disabled={!!busy} onClick={() => void decide('decline')}>
            {busy === 'decline' ? t('redeemApproval.declining') : t('redeemApproval.decline')}
          </Button>
          {/* Approving takes credits from the Player's balance: money out. */}
          <Button variant="payout" silent disabled={!!busy} onClick={() => void decide('approve')}>
            {busy === 'approve' ? t('redeemApproval.approving') : t('redeemApproval.approve')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <span className="self-start tabular-nums" aria-live="polite">
          <Badge tone={remaining <= 60 ? 'warning' : 'neutral'} dot={false}>
            {t('redeemApproval.expiresIn', { time: clock(remaining) })}
          </Badge>
        </span>
        <KeyValueList
          items={[
            { label: t('redeemApproval.creditsLabel'), value: <span className="tabular-nums">{credits}</span> },
            {
              label: t('redeemApproval.payoutLabel'),
              value: <Money amount={Number(shown.cash_paid) || 0} direction="out" locale={locale} />,
              emphasis: true,
            },
          ]}
        />
        <Alert tone="warning">{t('redeemApproval.warning')}</Alert>
        {requests.length > 1 ? (
          <p className="m-0 font-ui text-small text-tertiary">{t('redeemApproval.more', { count: requests.length - 1 })}</p>
        ) : null}
      </div>
    </AlertDialog>
  );
}
